(ns geir.constants
  "Constants common to all parts of the application.")

(def ^:const SQL-INTEGER-MAX-VALUE 2147483647)

;; This represents the highest value that we count when trying to determine how many
;; pages to tell the user about. If there are less than this number of things, we will
;; show them an exact page count. If there are this many things (or more), then we will
;; just show them 'Page N of Many', but they will still be able to page through them
;; all.
(def ^:const PAGINATOR-COUNT-LIMIT 1001)

;; If a network has more than this many flights, then we turn off certain features
;; because they would either not complete in a reasonable period of time and/or they
;; would negatively affect other users of the site. Currently we turn off sorting by
;; dates.
(def ^:const NETWORK-TOO-LARGE-LIMIT 900000)

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; Access mode is a property of Login. It represents a user's overall mode of
;; access to the UI.

;; Ordinary users must have explicit access to a network.
(def ^:const ACCESS-MODE-ORDINARY "ordy")

;; Omniscient users can access every network, but do not have edit or admin
;; permissions (unless there is a LoginNetwork_Map record that gives them that
;; level of access explicitly for that network)
(def ^:const ACCESS-MODE-OMNISCIENT "omns")

;; Omnipotent users can access every network with full permissions.
(def ^:const ACCESS-MODE-OMNIPOTENT "omnp")

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; Access level is a property of LoginNetwork_Map. It represents a user's level
;; of access to a particular network."

(def ^:const ACCESS-LEVEL-READ-ONLY "read")
(def ^:const ACCESS-LEVEL-AD-MANAGER "amgr")
(def ^:const ACCESS-LEVEL-EDITOR "edit")
(def ^:const ACCESS-LEVEL-ADMIN "admn")

(def ^:const ACCESS-LEVELS
  #{ACCESS-LEVEL-READ-ONLY
    ACCESS-LEVEL-AD-MANAGER
    ACCESS-LEVEL-EDITOR
    ACCESS-LEVEL-ADMIN})

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; Access level is a property of Auth API organization_roles access_level. It represents
;; whether the user has admin or read-only access to the organization.
(def ^:const ORG-ACCESS-LEVEL-READ-ONLY "read")
(def ^:const ORG-ACCESS-LEVEL-ADMIN "admin")

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; reservation status
(def ^:const PROPOSED   "proposed")
(def ^:const CONFIRMED  "reserved")
(def ^:const BOOKED     "booked")
(def ^:const TRAFFICKED "trafficked")
(def ^:const LOST       "lost")

(def reservation-statuses
  {PROPOSED       ::reservation-status-proposed
   CONFIRMED      ::reservation-status-confirmed
   BOOKED         ::reservation-status-booked
   TRAFFICKED     ::reservation-status-trafficked
   LOST           ::reservation-status-lost})

;; Goal types
(def ^:const IMPRESSIONS 1)
(def ^:const PERCENTAGE 2)
(def ^:const CLICKS 3)
(def ^:const EVEN 4)
(def ^:const CONVERSIONS 7)
(def ^:const REVENUE 8)
(def ^:const DAILY-REVENUE 9)
(def ^:const MONTHLY-REVENUE 10)

;; Distribution types
(def ^:const AUTO-BALANCED 1)
;; luckily, the same value as the percentage goal type
;; (def ^:const PERCENTAGE 2)
(def ^:const FIXED 3)

;; Selection algorithms
(def ^:const LOTTERY 0)
(def ^:const AUCTION 1)
(def ^:const AD-CHAIN-ORDERED 2)
(def ^:const AD-CHAIN-OPTIMIZED 3)
(def ^:const LOTTERY-WITH-OUTBID 4)

;; Creative Formats
(def ^:const CREATIVE-FORMAT-FILE 0)
(def ^:const CREATIVE-FORMAT-HTML 1)
(def ^:const CREATIVE-FORMAT-CUSTOM-TEMPLATE 2)
(def ^:const CREATIVE-FORMAT-RTB 3)
(def ^:const CREATIVE-FORMAT-STATIC-TEXT-LINK 4)

;; Ad types
(def ^:const STATIC-TEXT-LINK 20)

;; Report statuses
(def ^:const REPORT-STATUS-SUBMITTED 0)
(def ^:const REPORT-STATUS-IN-PROGRESS 1)
(def ^:const REPORT-STATUS-DONE 2)
(def ^:const REPORT-STATUS-ERROR 3)
(def ^:const REPORT-STATUS-TOO-LARGE 4)

;; Scheduled report repetition intervals
(def ^:const REPORT-REPEAT-NEVER 0)
(def ^:const REPORT-REPEAT-DAILY 1)
(def ^:const REPORT-REPEAT-WEEKLY 2)
(def ^:const REPORT-REPEAT-MONTHLY 3)

;; Scheduled report windows
(def ^:const REPORT-RUN-FIRST 0)
(def ^:const REPORT-RUN-SECOND 1)
(def ^:const REPORT-RUN-THIRD 2)
(def ^:const REPORT-RUN-FOURTH 3)

;; To-date reporting types
(def ^:const REPORT-TODATE-CUSTOM 0)
(def ^:const REPORT-TODATE-MONTH 2)
(def ^:const REPORT-TODATE-QUARTER 3)
(def ^:const REPORT-TODATE-YEAR 4)
(def ^:const REPORT-TODATE-TODAY 5)
(def ^:const REPORT-TODATE-YESTERDAY 6)
(def ^:const REPORT-TODATE-LAST-SEVEN-DAYS 7)
(def ^:const REPORT-TODATE-LAST-FOURTEEN-DAYS 8)
(def ^:const REPORT-TODATE-LAST-THIRTY-DAYS 9)
(def ^:const REPORT-TODATE-LAST-WEEK-SUN-SAT 10)
(def ^:const REPORT-TODATE-LAST-WEEK-MON-SUN 11)
(def ^:const REPORT-TODATE-LAST-MONTH 12)

(def ad-chain-short-names
  {AD-CHAIN-ORDERED      ::ad-chain-ordered-adj
   AD-CHAIN-OPTIMIZED    ::ad-chain-optimized-adj})

(def todate-type
  {REPORT-TODATE-CUSTOM                ::report-todate-custom
   REPORT-TODATE-MONTH                 ::report-todate-month
   REPORT-TODATE-QUARTER               ::report-todate-quarter
   REPORT-TODATE-YEAR                  ::report-todate-year
   REPORT-TODATE-TODAY                 ::report-todate-today
   REPORT-TODATE-YESTERDAY             ::report-todate-yesterday
   REPORT-TODATE-LAST-SEVEN-DAYS       ::report-todate-last-seven-days
   REPORT-TODATE-LAST-FOURTEEN-DAYS    ::report-todate-last-fourteen-days
   REPORT-TODATE-LAST-THIRTY-DAYS      ::report-todate-last-thirty-days
   REPORT-TODATE-LAST-WEEK-SUN-SAT     ::report-todate-last-week-sun-sat
   REPORT-TODATE-LAST-WEEK-MON-SUN     ::report-todate-last-week-mon-sun
   REPORT-TODATE-LAST-MONTH            ::report-todate-last-month})

(def repeat-type
  {REPORT-REPEAT-NEVER    ::report-repeat-never
   REPORT-REPEAT-DAILY    ::report-repeat-daily
   REPORT-REPEAT-WEEKLY   ::report-repeat-weekly
   REPORT-REPEAT-MONTHLY  ::report-repeat-monthly})

(def run-type
  {REPORT-RUN-FIRST       ::report-run-first
   REPORT-RUN-SECOND      ::report-run-second
   REPORT-RUN-THIRD       ::report-run-third
   REPORT-RUN-FOURTH      ::report-run-fourth})

(def goal-type
  ;; Using `array-map` here to preserve insertion order, so that we can control
  ;; the order in which the goal types are displayed in the UI.
  (array-map
    nil             ::goal-type-not-set
    IMPRESSIONS     ::goal-type-impressions
    PERCENTAGE      ::goal-type-percentage
    CLICKS          ::goal-type-clicks
    CONVERSIONS     ::goal-type-conversions
    REVENUE         ::goal-type-revenue
    MONTHLY-REVENUE ::goal-type-monthly-revenue
    DAILY-REVENUE   ::goal-type-daily-revenue))

(def trackable-goal-type
  #{IMPRESSIONS
    CLICKS
    CONVERSIONS
    REVENUE})

;; Rate types
(def ^:const FLAT      1)
(def ^:const CPM       2)
(def ^:const CPC       3)
(def ^:const CPA_VIEW  4)
(def ^:const CPA_CLICK 5)
(def ^:const CPA_BOTH  6)

(def cpa-rate-types
  #{CPA_VIEW CPA_CLICK CPA_BOTH})

(def rate-type-description
  {FLAT      ::rate-type-flat
   CPM       ::rate-type-cpm
   CPC       ::rate-type-cpc
   CPA_VIEW  ::rate-type-cpa-view
   CPA_CLICK ::rate-type-cpa-click
   CPA_BOTH  ::rate-type-cpa-both})

;; New status
(def ^:const STATUS-ONTRACK           1)
(def ^:const STATUS-BORDERLINE        2)
(def ^:const STATUS-INDANGER          3)
(def ^:const STATUS-DELIVERING        4)
(def ^:const STATUS-PENDING           5)
(def ^:const STATUS-DISABLED          6)
(def ^:const STATUS-UNKNOWN           7)
(def ^:const STATUS-FINISHED          8)
(def ^:const STATUS-ENDED             9)
(def ^:const STATUS-PAUSED           10)
(def ^:const STATUS-SCHEDULED-PAUSED 11)
(def ^:const STATUS-UNDERDELIVERED   12)

;; How far back (in days) we look for past-end flights when computing
;; STATUS-FINISHED / STATUS-UNDERDELIVERED. Past this window, recently-ended
;; flights fall through to STATUS-ENDED so we don't fetch counts for ancient
;; flights on every page load.
(def ^:const STATUS-V2-PAST-END-LOOKBACK-DAYS 90)

(def ^:const DELIVERY-STATUS-HEALTHY          1)
(def ^:const DELIVERY-STATUS-BORDERLINE       2)
(def ^:const DELIVERY-STATUS-INDANGER         3)
(def ^:const DELIVERY-STATUS-FINISHED         4)
(def ^:const DELIVERY-STATUS-UNDERDELIVERED   5)

(def delivery-status
  {DELIVERY-STATUS-HEALTHY           ::delivery-status-healthy
   DELIVERY-STATUS-BORDERLINE        ::delivery-status-borderline
   DELIVERY-STATUS-INDANGER          ::delivery-status-indanger
   DELIVERY-STATUS-FINISHED          ::delivery-status-finished
   DELIVERY-STATUS-UNDERDELIVERED    ::delivery-status-underdelivered})

(def delivery-status-class
  {:disabled                      "inactive"
   :not-started                   "pending"
   :not-trackable                 "not-trackable"
   DELIVERY-STATUS-HEALTHY        "success"
   DELIVERY-STATUS-BORDERLINE     "borderline"
   DELIVERY-STATUS-INDANGER       "danger"
   DELIVERY-STATUS-FINISHED       "success"
   DELIVERY-STATUS-UNDERDELIVERED "danger"
   nil                            "pending"})

;; Auction flights created without eCPM Optimization settings will use the
;; following defaults during the auction.
;; KB https://dev.kevel.com/docs/auction-general-settings#section-flight-auction-general-settings
(def delivery-ecpm-defaults
  {:ecpm-optimize-period 7
   :ecpm-multiplier 1
   :floor-ecpm 0
   ;celing ecpm is none/infinity
   :ceiling-ecpm nil
   :default-ecpm 0
   :burn-in-impressions 2400})

;; Order matters here because it is the order of the dropdown items in the
;; frequency cap UI. (See app.ui.campaigns.flights.edit, for example.)
;; 8/6/18 added third element which is freqcapduration limit for each freqcap type
(def freq-cap-type
   [[0 ::freq-cap-none nil]
    [3 ::freq-cap-minute 4400]
    [1 ::freq-cap-hour 168]
    [2 ::freq-cap-day 180]])

(def selection-algorithm-kw
  {0 :lottery
   1 :auction
   2 :ad-chain-ordered
   3 :ad-chain-optimized})

(def ^:const CAP-TYPE-IMPRESSION 1)
(def ^:const CAP-TYPE-CLICK 2)
(def ^:const CAP-TYPE-CONVERSION 3)
(def ^:const CAP-TYPE-REVENUE 4)

;; Order matters here because it is the order of the dropdown items, and we want
;; "None" to be first.
(def cap-types
  [[nil                 ::cap-type-none]
   [CAP-TYPE-IMPRESSION ::cap-type-impression]
   [CAP-TYPE-CLICK      ::cap-type-click]
   [CAP-TYPE-CONVERSION ::cap-type-conversion]
   [CAP-TYPE-REVENUE    ::cap-type-budget]])

(def cap-type-plurals
  {1 ::cap-type-impressions
   2 ::cap-type-clicks
   3 ::cap-type-conversions
   4 ::cap-type-revenue})

(def campaign-delivery-status-messages
  {:disabled                         ::campaign-delivery-disabled
   :not-started                      ::campaign-delivery-not-started
   DELIVERY-STATUS-HEALTHY           ::campaign-delivery-healthy
   DELIVERY-STATUS-BORDERLINE        ::campaign-delivery-borderline
   DELIVERY-STATUS-INDANGER          ::campaign-delivery-indanger
   DELIVERY-STATUS-FINISHED          ::campaign-delivery-finished
   DELIVERY-STATUS-UNDERDELIVERED    ::campaign-delivery-underdelivered
   nil                               ::campaign-delivery-unavailable})

(def flight-delivery-status-messages
  {:disabled                        ::flight-delivery-disabled
   :not-trackable                   ::flight-delivery-not-trackable
   :not-started                     ::flight-delivery-not-started
   :flight-weights-healthy          ::flight-weights-delivery-healthy
   DELIVERY-STATUS-HEALTHY          ::flight-delivery-healthy
   DELIVERY-STATUS-BORDERLINE       ::flight-delivery-borderline
   DELIVERY-STATUS-INDANGER         ::flight-delivery-indanger
   DELIVERY-STATUS-FINISHED         ::flight-delivery-finished
   DELIVERY-STATUS-UNDERDELIVERED   ::flight-delivery-underdelivered
   nil                              ::flight-delivery-unavailable})

(def ad-delivery-status-messages
  {:disabled                        ::ad-delivery-disabled
   :not-trackable                   ::ad-delivery-not-trackable
   :not-started                     ::ad-delivery-not-started
   DELIVERY-STATUS-HEALTHY          ::ad-delivery-healthy
   DELIVERY-STATUS-BORDERLINE       ::ad-delivery-borderline
   DELIVERY-STATUS-INDANGER         ::ad-delivery-indanger
   DELIVERY-STATUS-FINISHED         ::ad-delivery-finished
   DELIVERY-STATUS-UNDERDELIVERED   ::ad-delivery-underdelivered
   nil                              ::ad-delivery-unavailable})


(def entity-key
  {"channel"    :channelid
   "campaign"   :campaignid
   "advertiser" :brandid})

(def creative-distribution-kw
  {false :auto-balance
   true  :override})

(def duplicate-modes
  {nil  ::duplicate-mode-not-set
   1    ::duplicate-mode-flight
   2    ::duplicate-mode-campaign
   3    ::duplicate-mode-advertiser
   4    ::duplicate-mode-creative})

(def ^:const KEVEL_IDP_ID
  "When this value is used in TheLounge_IdentityProvider.CognitoAppClientId,
   the identity provider is understood to be the Kevel default IdP. All
   other app clients (besides GOOGLE_IDENTITY_PROVIDER below) are assumed
   to be SSO integrations."

  "KEVEL_IDENTITY_PROVIDER")

(def ^:const GOOGLE_IDP_ID
  "When this value is used in TheLounge_IdentityProvider.CognitoAppClientId,
   the identity provider is understood to be Kevel's Google default IdP. All
   other app clients (besides KEVEL_IDENTITY_PROVIDER above) are assumed
   to be SSO integrations."

  "GOOGLE_IDENTITY_PROVIDER")

(def ^:const OBJECT-FIELD-VALUE-BYTE-LIMIT 1024)

;; Order matters here because it is the order of the dropdown items in the
;; attribution settings panel in the UI. (See app.ui.campaigns.flights.edit)
(def attribution-lookback-windows
  [[1 ::attribution-lookback-window-1]
   [7 ::attribution-lookback-window-7]
   [14 ::attribution-lookback-window-14]
   [30 ::attribution-lookback-window-30]])

(def attribution-match-types
  [["SameProduct" ::attribution-match-type-sameproduct]
   ["SameCategoryBrand" ::attribution-match-type-samecategorybrand]
   ["SameBrand" ::attribution-match-type-samebrand]
   ["SameMerchant" ::attribution-match-type-samemerchant]])

;; TODO: Clean this dumpster fire up
#?(:clj
   (do
     ;; [refactor] The clj version of selection-algorithm has a different shape
     ;; than the cljs version. It should have a different name.
     (def selection-algorithm
       {LOTTERY {:s "Lottery" :k :lottery}
        AUCTION {:s "Auction" :k :auction}
        AD-CHAIN-ORDERED {:s "Ad Chain Ordered"
                          :k :ad-chain-ordered}
        AD-CHAIN-OPTIMIZED {:s "Ad Chain Optimized"
                            :k :ad-chain-optimized}
        LOTTERY-WITH-OUTBID {:s "Lottery with Outbid"
                             :k :lottery-with-outbid}})

     (def rate-type
       {:flat      1
        :cpm       2
        :cpc       3
        :cpa-view  4
        :cpa-click 5
        :cpa-both  6}))

   :cljs
   (do
     (def selection-algorithm
       {LOTTERY             ::selection-algorithm-lottery
        AUCTION             ::selection-algorithm-auction
        AD-CHAIN-ORDERED    ::selection-algorithm-ad-chain-ordered
        AD-CHAIN-OPTIMIZED  ::selection-algorithm-ad-chain-optimized
        LOTTERY-WITH-OUTBID ::selection-algorithm-lottery-with-outbid})

    (def rate-type
      {FLAT      ::rate-type-flat
       CPM       ::rate-type-cpm
       CPC       ::rate-type-cpc
       CPA_VIEW  ::rate-type-cpa-view
       CPA_CLICK ::rate-type-cpa-click
       CPA_BOTH  ::rate-type-cpa-both})))

;; Duplicate flight useexisitngcreative values
(def ^:const DUPLICATE-FLIGHT-CREATIVES-NEW "new")
(def ^:const DUPLICATE-FLIGHT-CREATIVES-EXISTING "existing")
(def ^:const DUPLICATE-FLIGHT-CREATIVES-NONE "none")

;; Campaign approval status
(def ^:const APPROVAL_STATUS_ACTIVATE_REQUESTED   "activate-requested")
(def ^:const APPROVAL_STATUS_ACTIVATE_APPROVED    "activate-approved")
(def ^:const APPROVAL_STATUS_ACTIVATE_REJECTED    "activate-rejected")
(def ^:const APPROVAL_STATUS_DEACTIVATE_REQUESTED "deactivate-requested")
(def ^:const APPROVAL_STATUS_DEACTIVATE_APPROVED  "deactivate-approved")
(def ^:const APPROVAL_STATUS_DEACTIVATE_REJECTED  "deactivate-rejected")

(def campaign-approval-status-labels
  {APPROVAL_STATUS_ACTIVATE_REQUESTED   ::campaign-approval-status-activate-requested
   APPROVAL_STATUS_ACTIVATE_APPROVED    ::campaign-approval-status-activate-approved
   APPROVAL_STATUS_ACTIVATE_REJECTED    ::campaign-approval-status-activate-rejected
   APPROVAL_STATUS_DEACTIVATE_REQUESTED ::campaign-approval-status-deactivate-requested
   APPROVAL_STATUS_DEACTIVATE_APPROVED  ::campaign-approval-status-deactivate-approved
   APPROVAL_STATUS_DEACTIVATE_REJECTED  ::campaign-approval-status-deactivate-rejected
   nil                                  ::campaign-approval-status-none})

(def ^:const SITE_CUSTOM_RELEVANCY_SCORE_MIN 1)
(def ^:const SITE_CUSTOM_RELEVANCY_SCORE_MAX 1000)